import { SectionCard } from '@atlas/ui-components'
import { Button, Textarea } from '@atlas/ui-primitives'
import { useState } from 'react'
import { laneChangeRequests } from '@/domain/change-requests'
import type { ValidationLane } from '@/domain/workflow.types'
import { VALIDATION_LANE_LABEL } from '@/domain/workflow-stub.seed'
import {
	useChangeRequests,
	useCreateChangeRequest,
	useDeleteChangeRequest,
	useRemoveChangeRequestItem,
	useVerifyChangeRequestItem,
} from '@/hooks/useChangeRequests'
import { useCan } from '@/lib/capabilities'
import { REFUSAL, Restricted } from '@/lib/refusal'
import { notification } from '@/notifications/notification-store'
import { ChangeRequestCard } from './ChangeRequestCard'

const NOT_ON_THIS_LANE =
	'The order is not in this review right now — change requests are raised and verified while it is.'

/**
 * Change-request authoring for the current validation lane, restricted to the
 * Admin role (this system has no separate "PM" role).
 *
 * A lane holds any number of change requests, so the authoring control stays
 * available while requests already exist — submitting appends one rather than
 * replacing what is there. Each request renders through `ChangeRequestCard`,
 * which owns the item- and request-level removal controls. A non-Admin sees
 * every request and the authoring control locked, told why. Verifying is the reviewer's own tick,
 * separate from the worker's done tick in the production workspace, and it
 * is what resolves a request. Inspecting a validation step the order is not
 * on locks the verify ticks and authoring, told why, but keeps deletion.
 *
 * The source-material lane is the designer's, not an Admin's, so its screen
 * passes `canAuthor` itself, and drops the composer while only earlier
 * requests are left to sign off.
 */
export function ChangeRequestAuthor({
	workOrderId,
	lane,
	currentLane,
	canAuthor: canAuthorOverride,
	showComposer = true,
}: {
	workOrderId: string
	lane: ValidationLane
	/** The lane of the status the order actually sits on — not the step being inspected. */
	currentLane: ValidationLane | null
	/** Who may author and verify; defaults to the Admin role. */
	canAuthor?: boolean
	showComposer?: boolean
}) {
	const [draft, setDraft] = useState('')
	const changeRequests = laneChangeRequests(useChangeRequests(workOrderId).changeRequests, lane)
	const createRequest = useCreateChangeRequest(workOrderId)
	const verifyItem = useVerifyChangeRequestItem(workOrderId)
	const removeItem = useRemoveChangeRequestItem(workOrderId)
	const deleteRequest = useDeleteChangeRequest(workOrderId)
	const { canConfigure } = useCan()
	const canAuthor = canAuthorOverride ?? canConfigure
	const laneLabel = VALIDATION_LANE_LABEL[lane]
	// A request is raised on, and verified at, the order's current status only.
	const offLane = currentLane !== lane ? NOT_ON_THIS_LANE : undefined

	const create = () =>
		createRequest.mutate(
			draft
				.split('\n')
				.map((line) => line.trim())
				.filter(Boolean),
			{
				onSuccess: () => {
					setDraft('')
					notification.success(`Change request raised for ${laneLabel}.`)
				},
			},
		)

	const remove = (changeRequestId: string) =>
		deleteRequest.mutate(changeRequestId, {
			onSuccess: () => notification.success(`Change request deleted for ${laneLabel}.`),
		})

	return (
		<SectionCard
			title={`Change requests · ${laneLabel}`}
			count={changeRequests.length || undefined}
			className="bg-ds-surface p-4"
		>
			{changeRequests.length ? (
				<ul className={showComposer ? 'mb-4 space-y-3' : 'space-y-3'}>
					{changeRequests.map((changeRequest) => {
						const { changeRequestId } = changeRequest
						return (
							<ChangeRequestCard
								key={changeRequestId}
								changeRequest={changeRequest}
								tick="verified"
								onToggleItem={
									canAuthor ? (itemId, verified) => verifyItem.mutate({ changeRequestId, itemId, verified }) : undefined
								}
								onRemoveItem={canAuthor ? (itemId) => removeItem.mutate({ changeRequestId, itemId }) : undefined}
								onDelete={canAuthor ? () => remove(changeRequestId) : undefined}
								lockedReason={canAuthor ? undefined : REFUSAL.verifyChangeRequest}
								tickLockedReason={offLane}
							/>
						)
					})}
				</ul>
			) : null}
			{showComposer ? (
				<Restricted
					allowed={canAuthor && !offLane}
					why={canAuthor ? (offLane ?? '') : REFUSAL.raiseChangeRequest}
					className="w-full flex-col items-start"
				>
					<Textarea
						size="sm"
						aria-label={`Author a change request for ${laneLabel}`}
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						placeholder="One task per line…"
						rows={3}
						readOnly={!canAuthor || !!offLane}
					/>
					<Button size="sm" className="mt-2" isDisabled={!draft.trim() || createRequest.isPending} onPress={create}>
						Create change request
					</Button>
				</Restricted>
			) : null}
		</SectionCard>
	)
}
